import {
  Avatar, Badge, Box, CircularProgress, Divider, IconButton, ListItemIcon, MenuItem, Menu as MuiMenu,
  Typography,
} from '@mui/material';
import React, { useCallback, useState, useEffect } from 'react';
import {
  Menu as MenuIcon, Logout, Wc, PersonAdd, PersonSearch, Settings, Person, NotificationsActive,
  LocalPizza,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import { useUser } from '../providers/UserProvider';
import logo from '../assets/logo.png';
import NotificationsDrawer from './NotificationsDrawer';

// Avatar that loads image async without blocking
const AsyncAvatar = ({ src, alt }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!src) {
      setError(true);
      return;
    }
    setLoaded(false);
    setError(false);
    const img = new Image();
    img.onload = () => setLoaded(true);
    img.onerror = () => setError(true);
    img.src = src;
  }, [src]);

  if (!src || error) {
    return <Avatar><Person /></Avatar>;
  }

  if (!loaded) {
    return (
      <Avatar>
        <CircularProgress size={16} />
      </Avatar>
    );
  }

  return <Avatar src={src} alt={alt} />;
};

const Menu = () => {
  const { user, unreadNotifications, setAddFollowerOpen, setFollowUserOpen, notificationsDrawerOpen, setNotificationsDrawerOpen } = useUser();
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();

  const handleClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const toggleNotificationsDrawer = useCallback(() => {
    setNotificationsDrawerOpen((prev) => !prev);
  }, [setNotificationsDrawerOpen]);

  const logout = useCallback(() => {
    const auth = getAuth();
    signOut(auth).then(() => {
      navigate('/');
    }).catch((error) => {
      console.error('Logout failed:', error);
    });
  }, [navigate]);

  return (
    <>
      <Box
        sx={(theme) => ({
          width: '100vw',
          backgroundColor: theme.palette.primary.dark,
          p: 1,
          px: 2,
          pt: 'calc(env(safe-area-inset-top) + 8px)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxSizing: 'border-box',
        })}
      >
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
        <img width={30} height={30} alt="Bodycount" src={logo} onClick={() => navigate('/')} style={{ cursor: 'pointer' }} />
        <Typography variant="h6" sx={{ color: 'white', flex: 1, textAlign: 'center' }}>Bodycount</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={toggleNotificationsDrawer}
            size="small"
            sx={{ color: 'white' }}
          >
            <Badge badgeContent={unreadNotifications} color="warning">
              <NotificationsActive />
            </Badge>
          </IconButton>
          <IconButton
            onClick={handleClick}
            size="small"
            aria-controls={anchorEl ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={anchorEl ? 'true' : undefined}
          >
            <MenuIcon sx={{ color: 'white' }} />
          </IconButton>
        </Box>
        <MuiMenu
          anchorEl={anchorEl}
          id="account-menu"
          open={Boolean(anchorEl)}
          onClose={handleClose}
          keepMounted
          disableScrollLock
          slotProps={{
            paper: {
              elevation: 3,
              sx: {
                mt: 1.5,
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={() => { handleClose(); if (user?.uid) navigate(`/user/${user.uid}`); }}>
            <AsyncAvatar src={user?.avatar} alt={user?.name} /> Profile
          </MenuItem>
          <MenuItem onClick={() => { handleClose(); navigate('/followers'); }}>
            <ListItemIcon>
              <Wc />
            </ListItemIcon>
            My Followers
          </MenuItem>
          <MenuItem onClick={() => { handleClose(); setAddFollowerOpen(true); }}>
            <ListItemIcon>
              <PersonAdd />
            </ListItemIcon>
            Add Follower
          </MenuItem>
          <MenuItem onClick={() => { handleClose(); setFollowUserOpen(true); }}>
            <ListItemIcon>
              <PersonSearch />
            </ListItemIcon>
            Follow Someone
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => { handleClose(); navigate('/settings'); }}>
            <ListItemIcon>
              <Settings />
            </ListItemIcon>
            Settings
          </MenuItem>
          <MenuItem
            onClick={() => { handleClose(); window.open('https://buymeacoffee.com/grahampyett', '_blank'); }}
            sx={(theme) => ({ color: theme.palette.primary.main })}
          >
            <ListItemIcon>
              <LocalPizza color="primary" />
            </ListItemIcon>
            Buy Me a Pizza
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => { handleClose(); logout(); }}>
            <ListItemIcon>
              <Logout />
            </ListItemIcon>
            Logout
          </MenuItem>
        </MuiMenu>
      </Box>
      <NotificationsDrawer open={notificationsDrawerOpen} onClose={() => setNotificationsDrawerOpen(false)} />
    </>
  );
};

export default Menu;
